import { Container, Group } from "@mantine/core"
import { Button, Card, LogoDark } from "@tfg/ui"
import { AxiosError } from "axios"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { httpClient } from "~/backend/axios"
import { TwoFactorForm } from "~/pages/Auth/components/TwoFactorForm"
import {
  EXPIRED_MAGIC_LINK,
  INVALID_MAGIC_LINK,
} from "~/pages/Auth/errors/expired-magic-link.error"
import { useVerifyTwoFactorMutation } from "~/pages/Auth/hooks"
import { resolvePostAuthPath } from "~/pages/Auth/utils/resolve-post-auth-path"
import { useAuth } from "~/store/auth-context"

type PendingTwoFactor = {
  readonly userId: string
  readonly destination: LibraryResponses.PostAuthDestination | undefined
  readonly fallbackRedirectUrl: string
}

export function MagicLink() {
  const { t } = useTranslation("account")
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { authenticate, isAuthenticated } = useAuth()
  const [url, setUrl] = useState<string>("")
  const [pendingTwoFactor, setPendingTwoFactor] =
    useState<PendingTwoFactor | null>(null)
  const { mutateAsync: verifyTwoFactor } = useVerifyTwoFactorMutation()

  const token = params.get("t")

  async function handleLogin() {
    httpClient
      .post<LibraryResponses.MagicLinkLogin>("/auth/login/magic-link", {
        token,
      })
      .then((res) => {
        if (res.status === 200) {
          if (res.data.twoFactor === true && res.data.userId) {
            setPendingTwoFactor({
              userId: res.data.userId,
              destination: res.data.destination,
              fallbackRedirectUrl: res.data.redirectUrl,
            })
            return
          }
          if (res.data.user) {
            authenticate(res.data.user)
            setUrl(
              resolvePostAuthPath({
                destination: res.data.destination,
                user: res.data.user,
                fallbackRedirectUrl: res.data.redirectUrl,
              })
            )
          } else {
            throw new Error("User not found")
          }
        }
      })
      .catch((err: AxiosError<LibraryResponses.MagicLinkLogin>) => {
        if (
          err?.response?.data &&
          typeof err.response.data === "object" &&
          "redirectUrl" in err.response.data &&
          typeof err.response.data.redirectUrl === "string"
        ) {
          const url = new URL(
            err.response.data.redirectUrl,
            window.location.origin
          )
          if (err.response.data.code === "EXPIRED_MAGIC_LINK") {
            url.searchParams.set("error", EXPIRED_MAGIC_LINK)
          }
          if (err.response.data.code === "INVALID_MAGIC_LINK") {
            url.searchParams.set("error", INVALID_MAGIC_LINK)
          }
          navigate("/login" + url.search)
        }
      })
  }

  const handleVerifyTwoFactor = async ({
    token: totpToken,
    userId,
  }: {
    token: string
    userId: string
  }): Promise<void> => {
    const user = await verifyTwoFactor({ token: totpToken, userId })
    authenticate(user)
    setUrl(
      resolvePostAuthPath({
        destination: pendingTwoFactor?.destination,
        user,
        fallbackRedirectUrl: pendingTwoFactor?.fallbackRedirectUrl ?? "/",
      })
    )
  }

  useEffect(() => {
    if (url && isAuthenticated) {
      navigate(url)
    }
  }, [isAuthenticated, url, navigate])

  useEffect(() => {
    if (!token) {
      navigate("/login")
    }
  }, [params, token, navigate, isAuthenticated, url])

  return (
    <Container
      fluid
      w="100vw"
      h="100vh"
      bg="var(--mantine-color-brand-background)"
    >
      <Group mb={100} align="center" justify="center" h="100vh">
        <Card>
          <Card.Section>
            <LogoDark width="20rem" />
            {pendingTwoFactor === null ? (
              <Button onClick={handleLogin}>
                {t("login.magicLink.button")}
              </Button>
            ) : (
              <TwoFactorForm
                userId={pendingTwoFactor.userId}
                onSubmit={handleVerifyTwoFactor}
              />
            )}
          </Card.Section>
        </Card>
      </Group>
    </Container>
  )
}
